import React, { type ReactElement } from 'react';
import {
  ApplicationIcon,
  Box,
  Button,
  ButtonIcon,
  Chip,
  FooterActionBar,
  Icon,
  Link as DSLink,
  Menu,
  SidePanel,
  Typography,
} from '@saviynt/design-system';

import SodFieldSet from '../../../../../../shared/components/SodFieldSet';
import {
  CF_NEU_STL,
  CF_SEC_BLD,
  ENTITLEMENT_DEATILS_TABS,
  violationsEntitlement,
} from '../../../../../../shared/constants';
import { useInvertedLocalize } from '../../../../../../shared/hooks';
import { type EntitlementRisksRowType } from '../data-layer/types';

import './InherentRisksSidePanel.css';

interface InherentRisksSidePanelProps {
  isSidePanelOpen: boolean;
  handleCloseSidePanel: () => void;
  selectedRow: EntitlementRisksRowType | null;
  handleSourceChange: (sourceType: string, activeTab: string) => void;
  initialOptionsWithIcons: { label: string; value: string; icon: string }[];
}

/**
 * * TODO
 * - Write a description
 * - Sidepanel kabob menu needs to be functional
 * - Sidepanel Chip color needs to be dynamic based on the detail value
 */
function InherentRisksSidePanel({
  isSidePanelOpen,
  handleCloseSidePanel,
  selectedRow,
  handleSourceChange,
  initialOptionsWithIcons,
}: InherentRisksSidePanelProps): ReactElement {
  const l = useInvertedLocalize();

  const inherentRisksSidePanelLayout = () => (
    <>
      {/* 3 Col Link Grid */}
      <Box className='InherentRisksSidePanel-detailLinks'>
        <Box className='InherentRisksSidePanel-detailLinkBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            Violating Entitlements
          </Typography>
          <DSLink
            kind='button'
            onClick={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DEATILS_TABS.TAB_VIOLATIONS
              );
            }}
            text={String(selectedRow?.violationsEntitlement)}
            size='large'
            prefixIconKind='Entitlement'
            suffixIconKind='ArrowRight'
          />
        </Box>
        <Box className='InherentRisksSidePanel-detailLinkBlock'>
          <Typography kind='body3' color={CF_NEU_STL}>
            Violations
          </Typography>
          <DSLink
            kind='button'
            onClick={() => {
              handleSourceChange(
                violationsEntitlement,
                ENTITLEMENT_DEATILS_TABS.TAB_VIOLATIONS
              );
            }}
            text={String(selectedRow?.violations)}
            size='large'
            prefixIconKind='SOD'
            suffixIconKind='ArrowRight'
          />
        </Box>
      </Box>
      {/* Conflicting Functions */}
      <Box className='InherentRisksSidePanel-detailLinks'>
        <SodFieldSet
          label='Conflicting Job Functions'
          value={
            <Box className='InherentRisksSidePanel-detailLinks-functions'>
              {selectedRow?.conflicts?.map((item: string) => (
                <Box
                  key={item}
                  className='InherentRisksSidePanel-detailLinks-functions-item'>
                  <Chip
                    label=''
                    color='warning'
                    kind='subtleFilled'
                    size='xSmall'
                    leftIcon={<Icon kind='JobFunction' />}
                  />
                  <Typography
                    kind='body2-bold'
                    color={CF_SEC_BLD}
                    lineLimit={1}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          }
        />
      </Box>
      {/* Field Sets */}
      <Box className='InherentRisksSidePanel-contentBody'>
        <Box className='InherentRisksSidePanel-contentBody-rowItem'>
          <SodFieldSet
            label='Risk Score'
            value={
              <Chip
                size='xSmall'
                kind='subtleFilled'
                label={`Critical (${selectedRow?.critical ?? 0})`}
                color='pink'
              />
            }
          />
        </Box>
        <Box className='InherentRisksSidePanel-contentBody-rowItem'>
          <SodFieldSet
            label='Application'
            value={
              <Box
                className='Sod-EntitlementSummary-rowItem'
                id='Sod-EntitlementSummary-rowItem-application'>
                <ApplicationIcon
                  logo={selectedRow?.application}
                  size='xxSmall'
                />
                <Typography kind='body2' color={CF_SEC_BLD}>
                  {selectedRow?.application}
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box className='InherentRisksSidePanel-contentBody-rowItem'>
          <SodFieldSet
            label='Risk Description'
            value={
              <Box tag='span' className='Sod-EntitlementSummary-rowItem'>
                <Typography kind='body2' color={CF_SEC_BLD} lineLimit={2}>
                  {selectedRow?.riskDescription}
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box className='InherentRisksSidePanel-contentBody-rowItem'>
          <SodFieldSet
            label='Rule Set'
            value={
              <Box tag='span' className='Sod-EntitlementSummary-rowItem'>
                <Typography kind='body2' color={CF_SEC_BLD}>
                  {selectedRow?.ruleSet}
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box className='InherentRisksSidePanel-contentBody-rowItem'>
          <SodFieldSet
            label='Business Process'
            value={
              <Box tag='span' className='Sod-EntitlementSummary-rowItem'>
                <Typography kind='body2' color={CF_SEC_BLD}>
                  {selectedRow?.businessProcess}
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box className='InherentRisksSidePanel-contentBody-rowItem'>
          <SodFieldSet
            label='Risk Priority'
            value={
              <Chip
                size='xxSmall'
                kind='subtleFilled'
                label={selectedRow?.priority}
                color='orange'
              />
            }
          />
        </Box>
        <Box className='InherentRisksSidePanel-contentBody-rowItem'>
          <SodFieldSet
            label='Risk Type'
            value={
              <Box tag='span' className='Sod-EntitlementSummary-rowItem'>
                <Typography kind='body2' color={CF_SEC_BLD}>
                  {selectedRow?.riskType}
                </Typography>
              </Box>
            }
          />
        </Box>
        <Box className='InherentRisksSidePanel-contentBody-rowItem'>
          <SodFieldSet
            label='Risk Owner'
            value={
              <Box tag='span' className='Sod-EntitlementSummary-rowItem'>
                <Typography kind='body2' color={CF_SEC_BLD}>
                  {selectedRow?.riskOwner}
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>
    </>
  );

  const inherentRisksSidePanelFooter = () => (
    <FooterActionBar className='InherentRisksSidePanel-footer'>
      <Button type='button' kind='filled' size='medium'>
        {l('View Details')}
      </Button>
      <Menu
        options={initialOptionsWithIcons}
        onChange={() => {
          handleSourceChange(
            violationsEntitlement,
            ENTITLEMENT_DEATILS_TABS.TAB_VIOLATIONS
          );
        }}
        trigger={
          <ButtonIcon
            size='medium'
            kind='ghost'
            icon={<Icon kind='MoreVertical' />}
          />
        }
        placement='top-end'
      />
    </FooterActionBar>
  );

  if (isSidePanelOpen) {
    return (
      <Box className='InherentRisksSidePanel-sidePanelWrapper'>
        <SidePanel
          isOpen={isSidePanelOpen}
          onClose={handleCloseSidePanel}
          header={{
            title: selectedRow?.title,
            description: l('Inherent Risk for Entitlement'),
            icon: 'CriticalHighRiskHexagon',
          }}
          appIconSize='large'
          layout={inherentRisksSidePanelLayout()}
          footer={inherentRisksSidePanelFooter()}
          className='InherentRisksSidePanel-sidePanel'
          layoutClassName='InherentRisksSidePanel-sidePanelLayout'
          headerClassName='InherentRisksSidePanel-sidePanelHeader'
          innerContentClassName='InherentRisksSidePanel-sidePanelInnerContent'
          footerClassName='InherentRisksSidePanel-sidePanelFooter'
        />
      </Box>
    );
  }

  return <Box className='InherentRisksSidePanel-sidePanelCloseFiller' />;
}

export default InherentRisksSidePanel;
